/**
 * When the web asks for a consent (CO-D1, #399): **once, ever.** A person is asked only while no answer of
 * theirs is on record, whatever version of the text it was given for.
 *
 * One pure function over what `GET /api/me` reports for one consent. The server states facts and
 * applies no rule of its own about asking (ruling R8); this is the rule, and it is the web's. iOS has
 * its own copy of it (`ConsentRule` in GroundworkKit), and the two must agree on every row of the table
 * in `consentRule.test.ts`.
 *
 * | status    | any version, or none known |
 * |-----------|----------------------------|
 * | unasked   | ask: `never_asked`         |
 * | granted   | never ask                  |
 * | declined  | never ask                  |
 * | withdrawn | never ask                  |
 *
 * - **A no is an answer, and so is a withdrawal.** Nothing asks again, not at the next launch and not when
 *   the text changes. The way back is Settings › Privacy (CO-D3).
 * - **A grant holds across versions of its text** (CO-D2): the server's gate holds a grant of any version
 *   (`api/platform/consent.py`, `holds`). A change that widens what is shared is a new consent, not a new version.
 *
 * **A build that cannot show the text in force does not ask** (`shown` below `current`): it would record
 * an answer to an older text than the one in force. This is a tab left open across a deploy; reloading
 * fetches the new bundle, and that one asks. The decks and the server's constants move together
 * (`scripts/check_copy_decks.py`), so the guard is for the window of a deploy, not for a normal day.
 */

import type { ConsentState } from '../../api/consents'

/** Why the person is being asked: there is one reason left, that no answer is on record (CO-D1). */
export type ConsentAsk = { reason: 'never_asked' }

/**
 * @param state The consent as `GET /api/me` reports it; `undefined` when `me` has not answered (a
 *   gate that cannot tell does not ask).
 * @param shown The version of the text this build shows: `CONSENT_TEXT_VERSION` for the kind.
 */
export function nextConsentAsk(state: ConsentState | undefined, shown: number): ConsentAsk | null {
  if (!state) return null
  if (shown < state.current_version) return null
  // Any other status, one this build has not heard of included, is not asked rather than guessed at.
  return state.status === 'unasked' ? { reason: 'never_asked' } : null
}
